/**
 * Vendor "Mark off-site" for a stuck daily-work day (SHY-193).
 *
 * A worker who goes on-site and never marks off-site (forgot, phone died,
 * left early) leaves the day `inprogress` with `clock_out` null. The vendor
 * closes it from the day detail; these helpers decide when that action is
 * offered, what off-site time to propose, and how the audit line reads.
 *
 * Clock marks are bare `HH:mm` (or `HH:mm:ss`) strings with no date, matching
 * `daily_work_execution.clock_in` / `clock_out`.
 */

type ClockMarks = { clock_in: string | null; clock_out: string | null };

const CLOSABLE_BY_THE_VENDOR = ["assigned", "accepted", "inprogress", "completed"];

export function isVendorOffSiteEligible(
  execution: ClockMarks | null | undefined,
  dayStatus: string | null | undefined
): boolean {
  if (!execution?.clock_in || !CLOSABLE_BY_THE_VENDOR.includes(dayStatus ?? "")) return false;
  return !execution.clock_out || dayStatus !== "completed";
}

const parseMinutes = (time: string): number | null => {
  const [h, m] = time.split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
};

const formatMinutes = (total: number): string => {
  const wrapped = ((total % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  const m = Math.round(wrapped % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

/**
 * Proposed off-site time: the scheduled end (`clock_in + max_hours`) when the
 * day has a usable scheduled duration, otherwise `now`. Both inputs are HH:mm.
 */
export function defaultVendorOffSiteTime(input: {
  clockIn: string | null;
  maxHours: number | null | undefined;
  now: string;
}): string {
  const { clockIn, maxHours, now } = input;
  if (!clockIn || maxHours == null || !(maxHours > 0)) return now;
  const start = parseMinutes(clockIn);
  if (start === null) return now;
  return formatMinutes(start + Math.round(maxHours * 60));
}

const formatClock = (time: string): string => {
  const minutes = parseMinutes(time);
  if (minutes === null) return time;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};

/**
 * Clock marks carry no date, so an off-site time at or before the on-site
 * time is read by the payout ladder (SHY-186) as wrapping past midnight or as
 * an empty span. Warn before the vendor commits to one.
 */
export function vendorOffSiteTimeWarning(clockIn: string | null | undefined, clockOut: string): string | null {
  if (!clockIn || !clockOut) return null;
  const start = parseMinutes(clockIn);
  const end = parseMinutes(clockOut);
  if (start === null || end === null) return null;
  if (end === start) {
    return `${formatClock(clockOut)} is the same as the on-site time, so no hours can be worked out from it.`;
  }
  if (end < start) {
    return `${formatClock(clockOut)} is before the on-site time of ${formatClock(clockIn)}, so it will be read as the next day and the hours flagged for review.`;
  }
  return null;
}
